import React, { useEffect, useState } from 'react';
import { BarChart3, Clock3, Plus, Search, WalletCards } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Pagination } from '../../../shared/components/Pagination';
import { Skeleton } from '../../../shared/components/Skeleton';
import { TextInput } from '../../../shared/components/TextInput';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import type { PurchaseRequestStatus } from '../api';
import { CriticalityBadge, PurchaseStatusBadge } from '../components/PurchaseBadges';
import { formatDate, formatMoney } from '../components/format';
import { usePurchaseDashboard, usePurchaseRequests } from '../hooks';

const PAGE_SIZE = 25;
const STATUS_OPTIONS: Array<{ value: PurchaseRequestStatus | ''; label: string }> = [
  { value: '', label: 'Todos os status' },
  { value: 'draft', label: 'Rascunho' },
  { value: 'pending_approval', label: 'Aguardando aprovação' },
  { value: 'returned', label: 'Devolvida' },
  { value: 'approved', label: 'Aprovada' },
  { value: 'in_quotation', label: 'Em cotação' },
  { value: 'pending_selection_approval', label: 'Seleção em aprovação' },
  { value: 'finance_pending', label: 'Aguardando Financeiro' },
  { value: 'payment_in_progress', label: 'Pagamento em andamento' },
  { value: 'paid', label: 'Paga' },
  { value: 'closed', label: 'Encerrada' },
  { value: 'rejected', label: 'Rejeitada' },
  { value: 'cancelled', label: 'Cancelada' },
];

export default function ComprasPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PurchaseRequestStatus | ''>('');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(PAGE_SIZE);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const requests = usePurchaseRequests({ search: search || undefined, status, offset, limit });
  const canViewReports = can(FEATURES.comprasRelatorios, 'view');
  const dashboard = usePurchaseDashboard(canViewReports);
  const rows = requests.data?.data ?? [];

  return (
    <PageContainer
      title="Compras"
      subtitle="Solicitações, cotações, aprovações e pagamentos em uma única jornada auditável"
      action={can(FEATURES.comprasRequisicoes, 'create') ? (
        <Button icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/compras/solicitacoes/nova')}>Nova solicitação</Button>
      ) : undefined}
    >
      {canViewReports && <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Resumo de compras">
        <SummaryCard label="Solicitações" value={dashboard.data?.totalRequests} icon={<BarChart3 className="h-5 w-5" />} />
        <SummaryCard label="Aprovações pendentes" value={dashboard.data?.pendingApprovals} icon={<Clock3 className="h-5 w-5" />} />
        <SummaryCard label="Pagamentos pendentes" value={dashboard.data?.pendingPayments} icon={<WalletCards className="h-5 w-5" />} />
        <SummaryCard label="Economia estimada" value={formatMoney(dashboard.data?.estimatedSavings, dashboard.data?.estimatedSavingsCurrency ?? 'BRL')} icon={<BarChart3 className="h-5 w-5" />} />
      </section>}

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[250px] flex-1">
          <TextInput
            label="Buscar por número, título ou área"
            icon={<Search className="h-4 w-4" />}
            value={searchInput}
            onChange={(event) => { setSearchInput(event.target.value); setOffset(0); }}
          />
        </div>
        <div className="w-64">
          <AutocompleteSelect
            label="Status"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(value) => { setStatus(value as PurchaseRequestStatus | ''); setOffset(0); }}
          />
        </div>
      </div>

      {(requests.isError || (canViewReports && dashboard.isError)) && <Alert variant="error">Não foi possível carregar todos os dados de Compras.</Alert>}
      {requests.isLoading ? (
        <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
      ) : rows.length === 0 ? (
        <EmptyState title="Nenhuma solicitação encontrada" description="Crie uma solicitação ou ajuste os filtros da consulta." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Número</th><th className="px-3 py-2 text-left">Solicitação</th>
                <th className="px-3 py-2 text-left">Área</th><th className="px-3 py-2 text-right">Estimativa</th>
                <th className="px-3 py-2 text-left">Criticidade</th><th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Necessário até</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((request) => (
                <tr key={request.id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/compras/solicitacoes/${request.id}`)}>
                  <td className="px-3 py-3 font-medium text-gray-900">{request.requestNumber ?? request.code ?? 'Em geração'}</td>
                  <td className="px-3 py-3">{request.title}</td><td className="px-3 py-3">{request.requestingArea}</td>
                  <td className="px-3 py-3 text-right">{formatMoney(request.estimatedValue)}</td>
                  <td className="px-3 py-3"><CriticalityBadge criticality={request.criticality} /></td>
                  <td className="px-3 py-3"><PurchaseStatusBadge status={request.status} /></td>
                  <td className="px-3 py-3">{formatDate(request.neededBy)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {requests.data && <Pagination offset={offset} limit={limit} count={requests.data.total} itemLabel="solicitação" itemLabelPlural="solicitações" onChange={(value) => { setOffset(value.offset); setLimit(value.limit); }} />}
    </PageContainer>
  );
}

function SummaryCard({ label, value, icon }: { label: string; value: React.ReactNode; icon: React.ReactNode }) {
  return <div className="rounded-lg border border-gray-200 bg-white p-4"><div className="flex items-center gap-2 text-sm text-gray-500">{icon}{label}</div><p className="mt-2 text-2xl font-semibold text-gray-900">{value ?? '—'}</p></div>;
}
